import React, { useState } from 'react'
import RebateTreeTable, { rebateTreeTableColumns, transformRebateDataToTreeTable } from '../../../components/rebate-tree-table'
import { dummyDataForRebate } from '../../../utils/constants'
import RebateInfo from '../../../components/progress-table/RebateInfo'
import TierTable from '../../../components/progress-table/TierTable'
import { CheckPicker, Loader, SelectPicker } from 'rsuite'
import moment from 'moment'
import useResponsiveWidth from '../../../react-query/hooks/useResponsiveWidth'
import ProgressBarCell from '../../../components/progress-bar'
import { numberFormatterForDecimalInMNew } from '../../../utils/formatter'
import { useRebateTrackingDetails, useRebateTrackingFilters } from '../../../react-query/hooks/HRebateTracking'
import Button from '../../../components/button'
import { sortCommodityFirst } from '../../../utils/helpers/commodityOrder'


type Tier = {
    startValue: number | null | undefined;
    endValue: number | null | undefined;
    benefitPercentage: number | null | undefined;
};
type TierBracketResult = {
    start: number;
    end: number;
    benefitPercentage: number;
} | number;


const ProjectionLegend: React.FC = () => (
    <div className="flex mt-1 ml-2 gap-[2px] text-[10px] text-gray-800">
        <div className="flex items-center gap-2 leading-normal">
            <span className="inline-block h-2 w-2 rounded-sm bg-[#64C850]" />
            <span>Projected above 110% of threshold</span>
        </div>
        <div className="flex items-center gap-2 leading-normal">
            <span className="inline-block h-2 w-2 rounded-sm bg-[#FFBF00]" />
            <span>Projected between 90%-110% of threshold</span>
        </div>
        <div className="flex items-center gap-2 leading-normal">
            <span className="inline-block h-2 w-2 rounded-sm bg-[#CC5500]" />
            <span>Projected less than 90% of threshold</span>
        </div>
    </div>
);

const LviaTracking = () => {
    const width = useResponsiveWidth();
    const [filters, setFilters] = React.useState<any>({
        commodity: [],
        districts: [],
        vendors: [],
        category: []
    });

    const [detailsFilters, setDetailsFilters] = React.useState<any>({
        commodity: [],
        districts: [],
        vendors: [],
        category: []
    });

    const [commodity, setCommodity] = useState<string[]>([])
    const [districts, setDistricts] = useState<string[]>([])
    const [category, setCategory] = useState<string[]>([])
    const [vendors, setVendors] = useState<string[]>([])
    const [selectedRow, setSelectedRow] = useState<any>(null)
    const [expectedRebate, setExpectedRebate] = useState<number>(0)
    const [totalPurchaseInput, setTotalPurchaseInput] = useState<number>(0)
    const { rebateTrackingDetails, rebateTrackingDetailsLoading } = useRebateTrackingDetails(detailsFilters.commodity, detailsFilters.districts, detailsFilters.vendors, detailsFilters.category)
    const { fetchFilters } = useRebateTrackingFilters(localStorage.getItem("userEmail"), filters.commodity, filters.districts, filters.vendors, filters.category)


    const getTierBracket = (
        currentPurchase: number,
        tierDetails: Tier[]
    ): any => {
        if (!Array.isArray(tierDetails) || tierDetails.length === 0) {
            return 0;
        }

        const tiers = [...tierDetails].sort(
            (a, b) => Number(a.startValue ?? 0) - Number(b.startValue ?? 0)
        );

        for (const tier of tiers) {
            const start = Number(tier.startValue ?? 0);
            const end = Number(tier.endValue ?? start);

            if (currentPurchase >= start && currentPurchase < end) {
                return {
                    nextTierRebate: Number(tier.benefitPercentage ?? 0),
                    value: tier.endValue
                };
            }
        }

        return 0;
    };

    const nextTierRebateValue = selectedRow ? getTierBracket(selectedRow.currentPurchase, selectedRow.tierDetails) : 0

    const handleFilterClean = (
        key: "commodity" | "districts" | "vendors" | "category"
    ) => {
        setFilters((prev: any) => {
            const next = { ...prev, [key]: [] };

            // if ALL filters are now empty, also clear detailsFilters
            if (isFiltersEmpty(next)) {
                setDetailsFilters(next); // triggers hook with empty filters
            }

            return next;
        });
    };

    const resetFilter = () => {
        setSelectedRow(null)
        setCommodity([])
        setDistricts([])
        setCategory([]);
        setVendors([])
        setFilters({
            commodity: [],
            districts: [],
            vendors: [],
            category: []
        });

        setDetailsFilters({
            commodity: [],
            districts: [],
            vendors: [],
            category: []
        });
    }


    const appliFilter = () => {
        setSelectedRow(null)
        setDetailsFilters({
            commodity: commodity,
            districts: districts,
            vendors: vendors,
            category: category
        })
    }



    const isFiltersEmpty = (f: {
        commodity: any;
        districts: any[];
        vendors: any[];
        category: any[];
    }) => {
        const isEmptyValue = (v: any) => {
            if (Array.isArray(v)) return v.length === 0;
            return v === "" || v === null || v === undefined;
        };

        return (
            isEmptyValue(f.commodity) &&
            isEmptyValue(f.districts) &&
            isEmptyValue(f.vendors) &&
            isEmptyValue(f.category)
        );
    };


    const calculateExpectedRebate = () => {
        if (!selectedRow) return;

        const backToZero = selectedRow.backToZero?.toLowerCase?.() ?? "";
        const tiers = selectedRow.tierDetails ?? [];

        if (backToZero === "yes") {
            const currentPurchase = Number(selectedRow.currentPurchase || 0);

            // find tier(s) where currentPurchase lies between start & end
            // and take the highest benefitPercentage
            let rebatePct = 0;
            for (const tier of tiers) {
                const start = Number(tier.startValue ?? 0);
                const end = Number(tier.endValue ?? 0)

                if (totalPurchaseInput >= start && totalPurchaseInput < end) {
                    rebatePct = Number(tier.benefitPercentage ?? 0)
                }
            }

            const value = rebatePct * Number(totalPurchaseInput || 0);
            setExpectedRebate(value / 100);
            return;
        }

        if (backToZero === "no") {
            const currentSpend = selectedRow.currentPurchase
            if (!currentSpend || !tiers.length) {
                setExpectedRebate(0);
                return;
            }

            // rebate_earned_on logic per tier
            let rebateBase = 0;
            for (const tier of tiers) {
                const start = Number(tier.startValue ?? 0);
                const end = Number(tier.endValue ?? start);
                if (totalPurchaseInput >= start && totalPurchaseInput >= end) {
                    rebateBase += ((end - start) * tier.benefitPercentage) / 100; //0
                } else if (totalPurchaseInput >= start && totalPurchaseInput < end) {
                    rebateBase += ((totalPurchaseInput - start) * tier.benefitPercentage) / 100;
                }
            }

            const value = rebateBase;
            setExpectedRebate(value);
        }
    };

    return (
        <>
            {rebateTrackingDetailsLoading ? <Loader
                backdrop
                content="Loading..."
                size="md"
                vertical
                className="text-black font-bold"
                style={{ opacity: 0.8, zIndex: 9999 }}
            /> : <div className={`flex flex-col h-full overflow-hidden mb-[20vh]`}>
                <div className="ps-[3%] flex pr-[5%] pt-[0] shadow-normal relative">
                    <div className="flex justify-between items-center w-full">
                        <div className="font-normal text-[2vw] leading-[1vw]">
                            LVIA Rebate Tracking
                            <br />
                            <div className='flex flex-col gap-2 mt-6'>
                                <span className="font-bold text-fs16 text-[#858585]">
                                    Negotiation Year:&nbsp;
                                    <span className="font-bold underline text-deloitteGreen">
                                        {moment().utc().format("YYYY")}
                                    </span>
                                </span>
                                <span className="font-bold text-fs16 text-[#858585]">
                                    LVIA Progress as of:&nbsp;
                                    <span className="font-bold underline text-deloitteGreen">
                                        {moment().utc().format("MM/DD/YYYY")}
                                    </span>
                                </span>
                            </div>

                        </div>

                        <div
                            className={`py-[2%] flex flex-col justify-start items-center gap-4`}
                        >
                            <div className={`flex flex-col justify-start items-center gap-2`}>
                                <div className='flex gap-4 items-center'>
                                    <div className="flex flex-col justify-start items-start">
                                        <label className="font-bold text-[0.8vw]">Commodity</label>
                                        <CheckPicker
                                            className="dropdown-responsive-width"
                                            data={sortCommodityFirst(fetchFilters.commodity)}
                                            preventOverflow={true}
                                            style={{ width: width }}
                                            onClean={() => {
                                                setCommodity([]);
                                                handleFilterClean("commodity");
                                            }}
                                            onChange={(value: string[]) => {
                                                // if (value.length > 0) {
                                                setCommodity(value);
                                                // }
                                                if (value.length === 0) {
                                                    handleFilterClean("commodity");
                                                }
                                            }}
                                            onClose={() => {
                                                setFilters((prev: any) => ({
                                                    ...prev,
                                                    commodity: commodity ? commodity : [],
                                                }));
                                            }}
                                            cleanable={true}
                                            placeholder="All"
                                            value={commodity ?? ""}
                                            labelKey="commodity"
                                            valueKey="commodity"
                                            menuMaxHeight={150}
                                        />
                                    </div>
                                    <div className="flex flex-col justify-start items-start">
                                        <label className="font-bold text-[0.8vw]">Category</label>
                                        <CheckPicker
                                            className="dropdown-responsive-width"
                                            data={fetchFilters.categories}
                                            preventOverflow={true}
                                            style={{ width: width }}
                                            onClean={() => {
                                                setCategory([]);
                                                handleFilterClean("category");

                                            }}
                                            onChange={(value: string[]) => {
                                                // if (value.length > 0) {
                                                setCategory(value);
                                                // }
                                                if (value.length === 0) {
                                                    handleFilterClean("category");
                                                }
                                            }}
                                            onClose={() => {
                                                setFilters((prev: any) => ({
                                                    ...prev,
                                                    category: category ? category : [],
                                                }));

                                            }}
                                            cleanable={true}
                                            placeholder="All"
                                            value={category ?? ""}
                                            labelKey="category"
                                            valueKey="category"
                                            menuMaxHeight={150}
                                        />
                                    </div>
                                    <Button
                                        className="w-[5vw] mt-4"
                                        label={"Reset"}
                                        bgColor={"#86BC25"}
                                        disabled={isFiltersEmpty(filters)}
                                        onClick={resetFilter}
                                    />
                                </div>
                                <div className='flex gap-4 items-center'>
                                    <div className="flex flex-col justify-start items-start">
                                        <label className="font-bold text-[0.8vw]">District</label>
                                        <CheckPicker
                                            sticky
                                            className="dropdown-responsive-width"
                                            countable={true}
                                            data={fetchFilters.districts}
                                            preventOverflow={true}
                                            style={{ width: width }}
                                            onClean={() => {
                                                setDistricts([]);
                                                handleFilterClean("districts");

                                            }}
                                            onChange={(value: string[]) => {
                                                // if (value.length > 0) {
                                                setDistricts(value);
                                                // }
                                                if (value.length === 0) {
                                                    handleFilterClean("districts");
                                                }
                                            }}
                                            onClose={() => {
                                                setFilters((prev: any) => ({
                                                    ...prev,
                                                    districts: districts.length > 0 ? districts : [],
                                                }));

                                            }}
                                            cleanable={true}
                                            value={districts ?? []}
                                            placeholder="All"
                                            labelKey="district"
                                            valueKey="district"
                                            menuMaxHeight={150}
                                        />
                                    </div>

                                    <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
                                        <label className="font-bold text-[0.8vw]">Supplier</label>
                                        <CheckPicker
                                            sticky
                                            data={fetchFilters.vendor}
                                            preventOverflow={true}
                                            style={{ width: width }}
                                            onOpen={() => { }}
                                            onChange={(value: string[]) => {
                                                // if (value.length > 0) {
                                                setVendors(value);
                                                // }
                                                if (value.length === 0) {
                                                    handleFilterClean("vendors");
                                                }
                                            }}
                                            onClose={() => {
                                                setFilters((prev: any) => ({
                                                    ...prev,
                                                    vendors: vendors.length > 0 ? vendors : [],
                                                }));
                                            }}
                                            onClean={() => {
                                                setVendors([]);
                                                handleFilterClean("vendors");

                                            }}
                                            value={vendors ?? []}
                                            placeholder="All"
                                            labelKey="vendor"
                                            valueKey="vendor"
                                            menuMaxHeight={150}
                                        />
                                    </div>
                                    <Button
                                        className="w-[5vw] mt-4"
                                        label={"Apply"}
                                        bgColor={"#86BC25"}
                                        onClick={appliFilter}
                                        disabled={isFiltersEmpty(filters)}
                                    />
                                </div>


                            </div>

                        </div>
                    </div>
                </div>

                <div
                    className={`bg-[#F1F1F1] p-[1rem] flex justify-start items-start h-full w-full`}
                >
                    <div className="flex gap-0 w-full h-full">
                        <div className="w-[60%] overflow-y-auto flex flex-col gap-2 h-full shadow-module-card ">
                            <div className='bg-white rounded-md border w-full border-[#BEBEBE]'>
                                {rebateTrackingDetails && rebateTrackingDetails?.allDistrictData && <RebateTreeTable height={'73vh'}
                                    onRowClick={(e) => {
                                        setSelectedRow(e)
                                        setExpectedRebate(0)
                                        setTotalPurchaseInput(0)
                                    }}
                                    value={selectedRow} data={transformRebateDataToTreeTable(rebateTrackingDetails?.allDistrictData)} columns={rebateTreeTableColumns} />}
                            </div>
                        </div>
                        {selectedRow ? <div className="flex h-[74vh] flex-col gap-1 w-[40%] px-2 py-4 bg-white shadow-module-card rounded-md border border-[#BEBEBE]">
                            <div className='flex flex-col gap-1 overflow-x-auto'>
                                <div className="flex gap-4 justify-between h-full w-full">
                                    <div className="w-full flex justify-between border border-gray-400 px-3 py-2 rounded-md">
                                        <span className="font-semibold">
                                            {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "dollars" ? "Dollar" : "Pound"} to Next Tier
                                        </span>
                                        {selectedRow?.dollarPoundToNextTier ? <span className="font-bold">
                                            {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "dollars" && "$"}
                                            {numberFormatterForDecimalInMNew(selectedRow?.dollarPoundToNextTier, 2)}
                                            {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "pounds" && " lbs."}
                                        </span> : <span className="font-bold">
                                            -
                                        </span>}
                                    </div>
                                    <div className="w-full flex text-fs18 justify-between border shadow-sm border-gray-400 px-3 py-2 rounded-md">
                                        <span className="font-semibold">
                                            Next Tier Benefit
                                        </span>

                                        {selectedRow?.backToZero && selectedRow?.backToZero.toLowerCase() === 'yes' && selectedRow.parentId === undefined ? <span className="font-bold">
                                            {/* {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "dollars" && "$"} */}
                                            {numberFormatterForDecimalInMNew((nextTierRebateValue?.value * Math.abs(nextTierRebateValue.nextTierRebate - Number(selectedRow.nextTierBenefit)) * (selectedRow?.growthTierType.toLowerCase() === "pounds" ? selectedRow.netSpend : 1)) / 100, 2, "$")}
                                            {/* // 2-1=1 */}
                                            {/* {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "pounds" && " lbs."} */}
                                        </span> : <span className="font-bold">
                                            -
                                        </span>}

                                    </div>
                                    <div></div>
                                </div>
                                <div className="pr-4">
                                    <RebateInfo
                                        district={selectedRow.district}
                                        category={selectedRow.category}
                                        supplier={selectedRow.supplier}
                                        backToZero={selectedRow.backToZero}
                                    />
                                </div>
                                <div className="flex gap-4 justify-between w-full pr-8">
                                    {/* <div className="w-full flex justify-between border border-gray-300 px-3 py-1 rounded-md">
                                    <span className="font-semibold">
                                        Rebate Tiers
                                    </span>
                                </div> */}
                                </div>
                                <div className={`w-[98%] pl-[2vw] ${selectedRow?.type === "Parents" ? "mt-1" : "mt-[-5px]"} pr-8`}>
                                    {selectedRow && <ProgressBarCell
                                        height={40}
                                        isTopValueView={true}
                                        decimal={2}
                                        row={selectedRow} />}
                                </div>

                                {selectedRow.type !== 'Parents' && <div className="w-full px-4 text-sm">
                                    <div className="w-full flex justify-between px-3 py-1">
                                        <span className="font-bold text-fs16">
                                            Rebate Benefits
                                        </span>
                                    </div>
                                    <div className="w-full border border-[#bebebe] overflow-hidden">
                                        <div className="h-1 bg-[#a6ce39] w-full"></div>
                                        <table className="w-full text-xs border-separate border-spacing-0">
                                            <tbody>
                                                <tr className="bg-[#ebeee5]">
                                                    <td className="font-bold py-1 px-4 border-r border-[#bebebe] w-[80%]">Current Purchases</td>
                                                    <td className="text-right py-1 px-4 font-normal w-1/2">
                                                        {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "dollars" && "$"}
                                                        {numberFormatterForDecimalInMNew(Number(selectedRow?.currentPurchase), 2)}
                                                        {selectedRow?.growthTierType && selectedRow?.growthTierType.toLowerCase() === "pounds" && " lbs."}
                                                    </td>
                                                </tr>
                                                <tr className="bg-white">
                                                    <td className="font-bold py-1 px-4 border-r border-[#bebebe]">Current Rebate Earned</td>
                                                    <td className="text-right py-1 px-4 font-normal">
                                                        {/* {selectedRow?.growthTierType.toLowerCase() === "dollars" && "$"} */}
                                                        {Number(selectedRow?.currentRebatePurchases) > 0 ? numberFormatterForDecimalInMNew(Number(selectedRow.currentRebatePurchases), 2, "$") : "0"}
                                                        {/* {selectedRow?.growthTierType.toLowerCase() === "pound" && " lbs."} */}
                                                    </td>
                                                </tr>
                                                <tr className="bg-[#ebeee5]">
                                                    <td className="font-bold py-1 px-4 border-r border-[#bebebe]">Projected Purchases</td>
                                                    <td className="text-right py-1 px-4 font-normal">
                                                        {selectedRow?.growthTierType && selectedRow?.growthTierType?.toLowerCase() === "dollars" && "$"}
                                                        {numberFormatterForDecimalInMNew(selectedRow.projectedPurchaseEnd, 2)}
                                                        {selectedRow?.growthTierType && selectedRow?.growthTierType?.toLowerCase() === "pounds" && " lbs."}
                                                    </td>
                                                </tr>
                                                <tr className="bg-white">
                                                    <td className="flex items-center justify-between font-bold py-1 px-4 border-r border-[#bebebe]">
                                                        Expected Rebate
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                min={0}
                                                                value={totalPurchaseInput}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    const { value } = e.target;          // value is a string
                                                                    if (value === "") {
                                                                        setTotalPurchaseInput(0);        // allow clearing
                                                                        return;
                                                                    }
                                                                    const num = Number(value);
                                                                    if (!Number.isNaN(num)) {
                                                                        setTotalPurchaseInput(num);      // only set when it’s a valid number
                                                                    }
                                                                }}
                                                                placeholder="Total Purchases"
                                                                className="text-xs border border-gray-300 rounded px-2 italic py-1 w-28 text-right"
                                                            />
                                                            <button onClick={calculateExpectedRebate} className="bg-gray-200 border border-[#bebebe] rounded px-3 py-1 text-xs font-semibold hover:bg-gray-300">
                                                                Calculate
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="text-right py-2 px-4 font-bold align-middle">
                                                        {/* {selectedRow?.growthTierType.toLowerCase() === "dollars" && "$"} */}
                                                        {expectedRebate > 0 ? numberFormatterForDecimalInMNew(expectedRebate * selectedRow.netSpend, 2, "$") : "0"}
                                                        {/* {selectedRow?.growthTierType.toLowerCase() === "pounds" && " lbs."} */}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>}

                                <div className="w-full px-4 text-sm">
                                    <div className="w-full flex justify-between px-3 py-1">
                                        <span className="font-bold text-fs16">
                                            Purchase Pace Information
                                        </span>
                                    </div>
                                    <div className="w-full border border-[#bebebe] overflow-hidden">
                                        <TierTable tierDetails={selectedRow.tierDetails} growthTierType={selectedRow?.growthTierType} decimal={2} />
                                    </div>
                                    <ProjectionLegend />

                                </div>
                            </div>
                        </div> : <div className="flex flex-col gap-3 w-[40%] px-2 py-6 bg-white shadow-module-card rounded-md border border-[#BEBEBE]">
                            <span className="flex h-full justify-center items-center font-normal text-[3vh]">
                                Select a supplier to view details
                            </span>
                        </div>}

                    </div>

                </div>

            </div>}
        </>


    )
}

export default LviaTracking